import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAppStore } from './stores/appStore';
import { useAuthStore } from './stores/authStore';
import { usePresets } from './hooks/usePresets';
import { usePrintCollection } from './hooks/usePrintCollection';
import { useTheme } from './hooks/useTheme';
import * as ds from './services/dataService';
import * as transformService from './services/transformService';
import { exportToZip, saveToCloud } from './services/exportService';
import { savePrintsToCloud } from './services/collectionService';
import type { FileItem, Product, PrintItem } from './stores/appStore';
import type { Transform } from './stores/types';
import { saveAs } from 'file-saver';

/* Components */
import { LoginScreen } from './components/LoginScreen';
import { Navbar } from './components/Navbar';
import { CloudProgress } from './components/CloudProgress';
import { BatchPuzzles } from './components/BatchPuzzles';
import { CloudSaver } from './components/CloudSaver';
import { Gallery } from './components/Gallery';
import { EditorView } from './components/EditorView';
import { UpdatesModal } from './components/UpdatesModal';
import type { UpdateItem } from './services/dataService';

export default function App() {
  const auth = useAuthStore();
  const store = useAppStore();

  const {
    files,
    products,
    activeTab,
    galleryTab,
    cloudMode,
    activeProductId,
    selectedPrint,
    mockupsPerRow,
    isExporting,
    isUploading,
    isCloudSaving,
    saveStatus,
    maskColor,
    showBatchPuzzles,
    transforms,
    productTransforms,
    password,
    setFiles,
    setProducts,
    setPrintsConfig,
    setActiveTab,
    setGalleryTab,
    setActiveProductId,
    setSelectedPrint,
    setMockupsPerRow,
    setIsExporting,
    setIsUploading,
    setIsCloudSaving,
    setSaveStatus,
    setMaskColor,
    setShowBatchPuzzles,
    setTransforms,
    setProductTransforms,
    setCloudProgress,
    setPassword,
  } = store;

  /* Hooks */
  const { presets, savePreset, deletePreset, getPreset, isSingleTransformPreset } = usePresets();
  const {
    printCollection,
    selectedPrintIds,
    addPrintToCollection,
    selectPrintInCollection,
    removePrintFromCollection,
    updateArticle,
    updatePrintName,
    removeByFileName,
    getPrintsByIds,
    updatePositions,
  } = usePrintCollection();

  const autoLoadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPrintLoadedRef = useRef(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [appUpdates, setAppUpdates] = useState<UpdateItem[]>([]);
  const [showUpdatesHistory, setShowUpdatesHistory] = useState(false);
  const [cloudToast, setCloudToast] = useState<string | null>(null);
  const cloudToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { theme, setTheme } = useTheme();

  const showCloudToast = useCallback((msg: string) => {
    setCloudToast(msg);
    if (cloudToastTimer.current) clearTimeout(cloudToastTimer.current);
    cloudToastTimer.current = setTimeout(() => setCloudToast(null), 3500);
  }, []);

  // Compute unseen updates count for badge
  const unseenUpdatesCount = useMemo(() => {
    const lastSeen = localStorage.getItem('witehik_last_seen_update');
    const lastSeenTime = lastSeen ? parseInt(lastSeen, 10) : 0;
    return appUpdates.filter((u) => u.created_at > lastSeenTime).length;
  }, [appUpdates, showUpdatesHistory]); // re-compute after modal closes

  /* ── Auth ── */
  useEffect(() => {
    auth.restoreSession();
  }, []);

  const handleLoginSuccess = useCallback(
    async (username: string, pwd: string): Promise<boolean> => {
      const ok = await auth.login(username, pwd);
      if (ok) setPassword(pwd);
      return ok;
    },
    [auth, setPassword],
  );

  /* ── Init data ── */
  const init = useCallback(async () => {
    if (!auth.isAuth) return;
    try {
      const data = await ds.initialize();
      const processed = (data.products || []).map((p: Product) => {
        if (p.tab) return p;
        return {
          ...p,
          tab: p.id.startsWith('custom_') ? ('products' as const) : ('mockups' as const),
        };
      });
      setFiles(data.files || []);
      setProducts(processed);
      setPrintsConfig(data.printsConfig || {});

      // Load updates for notification
      const updates = await ds.loadUpdates();
      setAppUpdates(updates);
    } catch (e) {
      console.error('Init error:', e);
    }
  }, [auth.isAuth, setFiles, setProducts, setPrintsConfig]);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    if (activeProductId === null && products.length > 0) {
      const first = products.find((p) => p.enabled);
      if (first) setActiveProductId(first.id);
    }
  }, [products, activeProductId, setActiveProductId]);

  /* ── Body scroll lock in editor mode ── */
  useEffect(() => {
    // Scroll to top on tab change to prevent stuck scroll position
    window.scrollTo(0, 0);

    if (auth.isAuth && activeTab !== 'base' && activeTab !== 'cloud') {
      document.body.classList.add('app-mode-editor');
    } else {
      document.body.classList.remove('app-mode-editor');
    }
    return () => {
      document.body.classList.remove('app-mode-editor');
    };
  }, [activeTab, auth.isAuth]);

  /* ── Print selection ── */
  const handleSelectPrint = useCallback(
    async (file: FileItem) => {
      isPrintLoadedRef.current = false;
      setSelectedPrint(file);
      try {
        await transformService.loadPrintWithConfig(
          file,
          products,
          setTransforms,
          setProductTransforms,
          autoLoadTimerRef,
          isPrintLoadedRef,
        );
      } catch (e: any) {
        console.error(e);
        alert(e.message);
      }
    },
    [products, setSelectedPrint, setTransforms, setProductTransforms],
  );

  /* ── Auto-save transforms ── */
  const triggerSaveConfig = useCallback(
    (
      printName: string,
      data: { transforms: Record<string, Transform>; productTransforms: Record<string, Transform> },
    ) => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      setSaveStatus('saving');
      saveTimeoutRef.current = setTimeout(async () => {
        const ok = await transformService.savePrintConfig(
          printName,
          data.transforms,
          data.productTransforms,
        );
        setSaveStatus(ok ? 'saved' : 'error');
      }, 1000);
    },
    [setSaveStatus],
  );

  useEffect(() => {
    if (!isPrintLoadedRef.current || !selectedPrint || !auth.isAuth) return;
    triggerSaveConfig(selectedPrint.name, { transforms, productTransforms });
  }, [transforms, productTransforms, selectedPrint, auth.isAuth, triggerSaveConfig]);

  /* ── Upload ── */
  const handleUploadFiles = useCallback(
    async (fileList: FileList) => {
      if (!fileList.length) return;
      setIsUploading(true);
      try {
        const uploaded = await ds.uploadFiles(fileList);
        if (uploaded) setFiles([...files, ...uploaded]);
        await init();
      } catch {
        alert('Ошибка загрузки');
      } finally {
        setIsUploading(false);
      }
    },
    [files, init, setFiles, setIsUploading],
  );

  /* ── Config save ── */
  const handleSaveConfig = useCallback(
    async (newProducts: Product[]) => {
      setProducts(newProducts);
      await ds.saveConfig(newProducts);
    },
    [setProducts],
  );

  /* ── Export ── */
  const handleExportZip = useCallback(async () => {
    if (!selectedPrint) return alert('Выберите картинку');
    setIsExporting(true);
    try {
      const blob = await exportToZip(
        selectedPrint,
        products,
        transforms,
        productTransforms,
        activeTab,
      );
      saveAs(blob, `mockups_${selectedPrint.name.split('.')[0]}.zip`);
    } catch (e: any) {
      alert('Ошибка экспорта: ' + e.message);
    } finally {
      setIsExporting(false);
    }
  }, [selectedPrint, products, transforms, productTransforms, activeTab, setIsExporting]);

  /* ── Cloud save ── */
  const handleSaveToCloud = useCallback(async () => {
    if (!selectedPrint) return alert('Выберите принт');
    const mode = activeTab === 'base' || activeTab === 'cloud' ? cloudMode : activeTab;
    setIsCloudSaving(true);
    try {
      await saveToCloud(
        selectedPrint,
        products,
        transforms,
        productTransforms,
        activeTab,
        cloudMode,
        setCloudProgress,
      );
      showCloudToast('Готово! Файлы сохранены в облако.');
      await init();
    } catch (e: any) {
      alert('Ошибка: ' + e.message);
    } finally {
      setIsCloudSaving(false);
      setCloudProgress({ total: 0, done: 0, current: '' });
    }
  }, [
    selectedPrint,
    products,
    transforms,
    productTransforms,
    password,
    activeTab,
    cloudMode,
    init,
    setIsCloudSaving,
    setCloudProgress,
    showCloudToast,
  ]);

  const handleSaveCollectionToCloud = useCallback(
    async (printIds: string[]) => {
      const prints = getPrintsByIds(printIds);
      if (!prints.length) return alert('Нет выбранных принтов');
      setIsCloudSaving(true);
      try {
        const enabled = products.filter((p) => p.enabled);
        if (!enabled.length) throw new Error('Нет включенных товаров');
        const mode = activeTab === 'base' || activeTab === 'cloud' ? cloudMode : activeTab;
        await savePrintsToCloud({
          prints,
          enabledProducts: enabled,
          mode,
          transforms,
          productTransforms,
          onProgress: setCloudProgress,
        });
        showCloudToast(`Готово! ${prints.length} принт(ов) сохранено.`);
        await init();
      } catch (e: any) {
        alert('Ошибка: ' + e.message);
      } finally {
        setIsCloudSaving(false);
        setCloudProgress({ total: 0, done: 0, current: '' });
      }
    },
    [
      products,
      transforms,
      productTransforms,
      password,
      activeTab,
      cloudMode,
      init,
      getPrintsByIds,
      setIsCloudSaving,
      setCloudProgress,
      showCloudToast,
    ],
  );

  /* ── Print collection ── */
  const handleAddPrintToCollection = useCallback(
    (file: FileItem) => {
      if (!file) return;
      try {
        addPrintToCollection(file, products, transforms, productTransforms, activeTab);
      } catch (e: any) {
        alert('Ошибка: ' + e.message);
      }
    },
    [addPrintToCollection, products, transforms, productTransforms, activeTab],
  );

  const handleSelectPrintInCollection = useCallback(
    async (printId: string) => {
      selectPrintInCollection(printId);
      const print = printCollection.find((p) => p.id === printId);
      if (!print) return;
      isPrintLoadedRef.current = false;
      const normalized = { ...print, type: (print.type || 'upload') as any };
      setSelectedPrint(normalized as any);
      try {
        await transformService.loadPrintWithConfig(
          normalized as any,
          products,
          setTransforms,
          setProductTransforms,
          autoLoadTimerRef,
          isPrintLoadedRef,
        );
      } catch (e) {
        console.error(e);
      }
    },
    [
      printCollection,
      products,
      selectPrintInCollection,
      setSelectedPrint,
      setTransforms,
      setProductTransforms,
    ],
  );

  /* ── Presets ── */
  const handleSavePreset = useCallback(
    (name: string, scope = 'single') => {
      const map = activeTab === 'products' ? productTransforms : transforms;
      if (scope === 'all') {
        savePreset(name, { ...map });
        return;
      }
      if (activeProductId) {
        savePreset(name, map[activeProductId] || { x: 0, y: 0, scale: 0.5, rotation: 0 });
      } else alert('Выберите мокап');
    },
    [activeTab, activeProductId, transforms, productTransforms, savePreset],
  );

  const handleApplyPreset = useCallback(
    (name: string, applyToAll = false) => {
      const preset = getPreset(name);
      if (!preset) return;
      if (isSingleTransformPreset(preset)) {
        if (applyToAll) {
          const target = activeTab === 'products' ? productTransforms : transforms;
          const next = { ...target };
          products.forEach((p) => {
            const ok =
              activeTab === 'products' ? p.tab === 'products' : !p.tab || p.tab === 'mockups';
            if (ok && p.enabled) next[p.id] = { ...(preset as Transform) };
          });
          activeTab === 'products' ? setProductTransforms(next) : setTransforms(next);
          if (selectedPrint) updatePositions(selectedPrint.id || selectedPrint.name, next);
        } else {
          if (!activeProductId) return alert('Выберите товар');
          const setter = activeTab === 'products' ? setProductTransforms : setTransforms;
          setter((prev: Record<string, Transform>) => ({
            ...prev,
            [activeProductId]: { ...(preset as Transform) },
          }));
          if (selectedPrint)
            updatePositions(selectedPrint.id || selectedPrint.name, {
              ...(selectedPrint as any).positions,
              [activeProductId]: { ...(preset as Transform) },
            });
        }
      } else {
        const setter = activeTab === 'products' ? setProductTransforms : setTransforms;
        setter((prev: Record<string, Transform>) => ({
          ...prev,
          ...(preset as Record<string, Transform>),
        }));
        if (selectedPrint)
          updatePositions(selectedPrint.id || selectedPrint.name, {
            ...(selectedPrint as any).positions,
            ...(preset as Record<string, Transform>),
          });
      }
    },
    [
      getPreset,
      isSingleTransformPreset,
      activeTab,
      activeProductId,
      products,
      transforms,
      productTransforms,
      setTransforms,
      setProductTransforms,
      selectedPrint,
      updatePositions,
    ],
  );

  const handleDeleteFile = useCallback(
    async (name: string) => {
      removeByFileName(name);
    },
    [removeByFileName],
  );

  const handleAddProduct = useCallback(async () => {
    const id = 'custom_' + Date.now();
    const newProd: Product = {
      id,
      name: 'Новый Мокап',
      category: 'Custom',
      enabled: true,
      image: '',
      mask: '',
      overlay: '',
      defaultPrefix: 'CUST',
      width: 2000,
      height: 2000,
      maskType: '',
      tab: activeTab as 'mockups' | 'products',
    };
    handleSaveConfig([...products, newProd]);
  }, [activeTab, products, handleSaveConfig]);

  const handleUpdateProductDPI = useCallback(
    (productId: string, newDPI: number) => {
      handleSaveConfig(products.map((p) => (p.id === productId ? { ...p, dpi: newDPI } : p)));
    },
    [products, handleSaveConfig],
  );

  /* ── Render ── */
  if (!auth.isAuth) return <LoginScreen onLogin={handleLoginSuccess} />;

  return (
    <div className="min-h-screen pb-60">
      <UpdatesModal updates={appUpdates} forceOpen={showUpdatesHistory} onClose={() => setShowUpdatesHistory(false)} />
      <CloudProgress progress={store.cloudProgress} isVisible={isCloudSaving} />
      <Navbar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onOpenBatchPuzzles={() => setShowBatchPuzzles(true)}
        onOpenUpdates={() => setShowUpdatesHistory(true)}
        updatesCount={unseenUpdatesCount}
        theme={theme}
        onSetTheme={setTheme}
      />
      <BatchPuzzles isOpen={showBatchPuzzles} onClose={() => setShowBatchPuzzles(false)} />

      <div className="max-w-[1920px] mx-auto px-4 pt-4 pb-4">
        {activeTab === 'cloud' ? (
          <CloudSaver files={files} />
        ) : activeTab === 'base' ? (
          <Gallery
            files={files}
            auth={{ password }}
            init={init}
            onAddToCollection={handleAddPrintToCollection}
            onDeleteFile={handleDeleteFile}
            activeSubTab={galleryTab}
            onSubTabChange={setGalleryTab}
            galleryType={galleryTab === 'publication' ? 'publication' : 'upload'}
          />
        ) : (
          <EditorView
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            products={products}
            files={files}
            transforms={transforms}
            productTransforms={productTransforms}
            setTransforms={setTransforms}
            setProductTransforms={setProductTransforms}
            activeProductId={activeProductId}
            setActiveProductId={setActiveProductId}
            selectedPrint={selectedPrint as PrintItem | null}
            selectedPrintIds={selectedPrintIds}
            printCollection={printCollection}
            mockupsPerRow={mockupsPerRow}
            setMockupsPerRow={setMockupsPerRow}
            isUploading={isUploading}
            isExporting={isExporting}
            isCloudSaving={isCloudSaving}
            saveStatus={saveStatus}
            maskColor={maskColor}
            setMaskColor={setMaskColor}
            presets={presets}
            auth={{ password }}
            onUploadFiles={handleUploadFiles}
            onSelectPrint={handleSelectPrint}
            onAddPrintToCollection={handleAddPrintToCollection}
            onSelectPrintInCollection={handleSelectPrintInCollection}
            onRemovePrintFromCollection={removePrintFromCollection}
            onUpdateArticle={updateArticle}
            onUpdatePrintName={updatePrintName}
            onSaveCollectionToCloud={handleSaveCollectionToCloud}
            onSavePreset={handleSavePreset}
            onDeletePreset={deletePreset}
            onApplyPreset={handleApplyPreset}
            onSaveConfig={handleSaveConfig}
            onExportZip={handleExportZip}
            onSaveToCloud={handleSaveToCloud}
            onAddProduct={handleAddProduct}
            onUpdateProductDPI={handleUpdateProductDPI}
            triggerSaveConfig={triggerSaveConfig}
            updatePositions={updatePositions}
          />
        )}
      </div>

      {/* Cloud save toast */}
      {cloudToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999]">
          <div className="flex items-center gap-3 px-5 py-3 bg-slate-900/95 border border-emerald-500/30 rounded-xl shadow-2xl shadow-emerald-500/10 backdrop-blur-md" style={{ animation: 'fadeInUp 0.3s ease-out' }}>
            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="text-white text-sm font-medium">{cloudToast}</span>
            <button
              onClick={() => setCloudToast(null)}
              className="ml-2 text-slate-500 hover:text-white transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
